import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterList from "@mui/icons-material/FilterList";
import { Alert, Box, LinearProgress, Tooltip } from "@mui/material";
import {
  DataGridPro,
  ExportCsv,
  FilterPanelTrigger,
  Toolbar,
  ToolbarButton,
} from "@mui/x-data-grid-pro";
import type { FC } from "react";
import { useFormContext } from "react-hook-form";
import type { ChannelName } from "../../../../../channels/types";
import type { ToTarget } from "../../../../../to/use-to-target";
import type { PersonInUnsavedSegmentWithChannels } from "../../../../server/supabase-segment-service";
import type { Segment } from "../../../../types";
import { useRecipientColumns } from "../../hooks/use-recipient-columns";
import { useSegmentRecipients } from "../../hooks/use-segment-recipients";

export const SegmentRecipientsDataGrid: FC<{
  filterChannel?: ChannelName;
  target_connection_type?: ToTarget["target_connection_type"];
}> = ({ filterChannel, target_connection_type }) => {
  const columns = useRecipientColumns({ filterChannel });
  const { getValues } = useFormContext<{ segment: Pick<Segment, "segment_json"> }>();

  const segment_json = getValues("segment.segment_json");

  const { data, loading, error } = useSegmentRecipients(
    segment_json,
    filterChannel,
    target_connection_type,
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">Unable to load related Communications</Alert>;

  return (
    <DataGridPro<PersonInUnsavedSegmentWithChannels>
      pagination
      pageSizeOptions={[10, 25, 50, 100]}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 50, page: 0 },
          rowCount: 0,
        },
      }}
      disableRowSelectionOnClick
      getRowHeight={() => "auto"}
      rows={data}
      columns={columns}
      slots={{ toolbar: SegmentToolbar }}
      showToolbar
    />
  );
};

const SegmentToolbar: FC = () => {
  return (
    <Toolbar>
      <Tooltip title="Filters">
        <FilterPanelTrigger render={<ToolbarButton size="small" />}>
          <FilterList fontSize="inherit" /> &#xA0; Filters
        </FilterPanelTrigger>
      </Tooltip>
      <Tooltip title="Download as CSV">
        <ExportCsv render={<ToolbarButton size="small" />}>
          <FileDownloadIcon fontSize="small" /> &#xA0; Export
        </ExportCsv>
      </Tooltip>
      <Box sx={{ flexGrow: 1 }} />
    </Toolbar>
  );
};
